"use client"

import { useRouter } from "next/navigation"

/**
 * Shared "back" behavior for every in-app back button. Most of these pages
 * hard-coded `router.push("/")`, so arriving at e.g. a commission or a
 * profile from anywhere other than the home feed (a notification, a shared
 * link, another detail page) sent you back to "/" instead of wherever you
 * actually came from — the exact complaint this fixes.
 *
 * Real back navigation (`router.back()`) only when there's app history to
 * go back to AND the referrer was this app — a fresh tab, a bookmark, or an
 * inbound link from another site has browser history (or none) that has
 * nothing to do with this visit, so `back()` there would leave the app
 * entirely or land on an unrelated earlier page. In those cases, and only
 * those, we fall back to `fallbackHref`.
 */
export function useSmartBack(fallbackHref: string = "/") {
  const router = useRouter()

  return () => {
    const cameFromHere =
      typeof document !== "undefined" &&
      !!document.referrer &&
      document.referrer.startsWith(window.location.origin)
    if (cameFromHere && window.history.length > 1) {
      // If history.back() lands nowhere (same-page entries, replaced URLs), the URL stays put:
      // fall through to the fallback so the button never looks dead.
      const before = window.location.href
      router.back()
      setTimeout(() => {
        if (window.location.href === before) router.push(fallbackHref)
      }, 600)
    } else {
      router.push(fallbackHref)
    }
  }
}
